import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { CategoryTree } from "@/api/types";

const TEN_MIN = 10 * 60 * 1000;

export const catalogKeys = {
  tree: ["catalog", "tree"] as const,
  category: (slug: string) => ["catalog", "category", slug] as const,
  brands: (slug: string) => ["catalog", "brands", slug] as const,
  banners: (slug: string) => ["catalog", "banners", slug] as const,
};

export function useCategoryTree() {
  return useQuery({ queryKey: catalogKeys.tree, queryFn: () => unwrap(api.GET("/api/v1/categories/")), staleTime: TEN_MIN });
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.category(slug ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/categories/{slug}/", { params: { path: { slug: slug! } } })),
    enabled: !!slug,
    staleTime: TEN_MIN,
  });
}

export function useBrands(categorySlug: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.brands(categorySlug ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/brands/", { params: { query: { category: categorySlug } } })),
    enabled: !!categorySlug,
    staleTime: TEN_MIN,
  });
}

export function useCategoryBanners(slug: string | undefined) {
  return useQuery({
    queryKey: catalogKeys.banners(slug ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/categories/{slug}/banners/", { params: { path: { slug: slug! } } })),
    enabled: !!slug,
    staleTime: 60_000,
  });
}

export interface FlatCategory {
  id: string;
  name: string;
  slug: string;
  level: number;
  path: string[];
  isLeaf: boolean;
  allowsEscrow: boolean;
}

/** Flatten the tree for search & pickers (includes the breadcrumb path). */
export function flattenTree(tree: CategoryTree[] | undefined): FlatCategory[] {
  const out: FlatCategory[] = [];
  (tree ?? []).forEach((root) => {
    out.push({
      id: root.id,
      name: root.name,
      slug: root.slug,
      level: 0,
      path: [root.name],
      isLeaf: root.children.length === 0,
      allowsEscrow: root.allows_escrow,
    });
    root.children.forEach((b) => {
      out.push({
        id: b.id,
        name: b.name,
        slug: b.slug,
        level: 1,
        path: [root.name, b.name],
        isLeaf: b.children.length === 0,
        allowsEscrow: b.allows_escrow,
      });
      b.children.forEach((l) =>
        out.push({
          id: l.id,
          name: l.name,
          slug: l.slug,
          level: 2,
          path: [root.name, b.name, l.name],
          isLeaf: true,
          allowsEscrow: l.allows_escrow,
        }),
      );
    });
  });
  return out;
}

/** The category tree with icons, accent colours and live counts (home sidebar, icon rail, full-screen browser). */
export function useCategoryMenu() {
  return useQuery({
    queryKey: ["catalog", "menu"],
    queryFn: () => unwrap(api.GET("/api/v1/categories/menu/")),
    staleTime: 5 * 60_000,
  });
}

/** Subcategory rails and tiles for a category page. */
export function useCategorySections(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["catalog", "sections", slug ?? ""],
    queryFn: () => unwrap(api.GET("/api/v1/categories/{slug}/sections/", { params: { path: { slug: slug! } } })),
    enabled: !!slug && enabled,
    staleTime: 60_000,
  });
}
